import { roundMoney, MONEY_TOLERANCE } from './formatters';

export type EstadoDiferenciaTesoreria = 'insuficiente' | 'cuadrado' | 'a_cuenta';

export interface ResultadoTotalesTesoreria {
    totalMedios: number;
    totalAplicado: number;
    diferencia: number;
    diferenciaInsuficiente: boolean;
    diferenciaSinSaldo: boolean;
    estado: EstadoDiferenciaTesoreria;
}

/** Suma medios de pago/cobro con redondeo a 2 decimales. */
export function calcularTotalMedios(importes: number[]): number {
    return roundMoney(importes.reduce((sum, imp) => sum + roundMoney(imp || 0), 0));
}

/** Monto pendiente imputable de un comprobante (deuda a cancelar, siempre ≥ 0). */
export function montoImputableComprobante(comp: { saldo?: number; importe?: number }): number {
    const saldo = roundMoney(Number(comp.saldo ?? 0));
    const importe = roundMoney(Number(comp.importe ?? 0));
    if (saldo > 0) return saldo;
    if (importe > 0) return importe;
    return roundMoney(Math.abs(saldo) || Math.abs(importe));
}

/** Cheque de cartera endosado (traído desde informe o agregado con nroCarga). */
export function esChequeEndosoCartera(pago: { tipoPago?: string; nroCarga?: number | null }): boolean {
    return (
        (pago.tipoPago || '').toLowerCase() === 'cheque' &&
        pago.nroCarga != null &&
        Number(pago.nroCarga) > 0
    );
}

/** Suma montos a aplicar en comprobantes con redondeo a 2 decimales. */
export function calcularTotalAplicado(montos: number[]): number {
    if (montos.length === 0) return 0;
    return roundMoney(montos.reduce((sum, m) => sum + roundMoney(m || 0), 0));
}

/** Totales y estado de diferencia usados en recibos de cobranza y órdenes de pago. */
export function calcularTotalesTesoreria(
    importesMedios: number[],
    montosAplicar: number[]
): ResultadoTotalesTesoreria {
    const totalMedios = calcularTotalMedios(importesMedios);
    const totalAplicado = calcularTotalAplicado(montosAplicar);
    const diferencia = roundMoney(totalMedios - totalAplicado);
    const diferenciaInsuficiente = diferencia < -MONEY_TOLERANCE;
    const diferenciaSinSaldo = Math.abs(diferencia) <= MONEY_TOLERANCE;
    const estado: EstadoDiferenciaTesoreria = diferenciaInsuficiente
        ? 'insuficiente'
        : diferenciaSinSaldo
            ? 'cuadrado'
            : 'a_cuenta';

    return {
        totalMedios,
        totalAplicado,
        diferencia,
        diferenciaInsuficiente,
        diferenciaSinSaldo,
        estado,
    };
}

/** Importe sugerido al abrir el diálogo de pago cuando falta monto real. */
export function calcularImporteSugeridoPago(
    diferencia: number,
    diferenciaInsuficiente: boolean
): number {
    if (!diferenciaInsuficiente) return 0;
    return roundMoney(Math.abs(diferencia));
}

/** Normaliza un importe de medio de pago/cobro antes de persistirlo en estado o API. */
export function normalizeImporteMedio(importe: number): number {
    return roundMoney(importe);
}

/**
 * Comportamiento anterior sin redondeo ni tolerancia.
 * Útil para tests de regresión del bug de decimales.
 */
export function calcularTotalesTesoreriaLegacy(
    importesMedios: number[],
    montosAplicar: number[]
): { totalMedios: number; totalAplicado: number; diferencia: number; bloqueaProceso: boolean } {
    const totalMedios = importesMedios.reduce((sum, imp) => sum + (imp || 0), 0);
    const totalAplicado = montosAplicar.reduce((sum, m) => sum + (m || 0), 0);
    const diferencia = totalMedios - totalAplicado;
    return {
        totalMedios,
        totalAplicado,
        diferencia,
        bloqueaProceso: diferencia < 0,
    };
}
